import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import {
  effectiveAgentCommission,
  effectiveOwnerShare,
  effectiveAgentRemit,
  effectiveOwnerPayable,
  effectiveAgentPayable,
  remainingAgentRemit,
  remainingOwnerPay,
  remainingAgentPay,
  bookingAgentSummary,
  effectiveOwnerCommissionPercent,
} from '../../utils/externalCommissionMetrics.js';
import Button from '../common/Button.js';
import { ArrowLeft, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';


const EditExternalCommission = () => {
  const { externalCommissionId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchRecord = useCallback(async () => {
    try {
      const refreshRes = await externalCommissionService.refreshSettlement(externalCommissionId).catch(() => null);
      const data = refreshRes?.data?.success
        ? refreshRes.data.data
        : (await externalCommissionService.getById(externalCommissionId)).data.data;
      setRecord(data);
      setRemarks(data.remarks || '');
    } catch {
      Alert.error('Failed to load commission record');
      navigate('/external-commissions');
    } finally {
      setLoading(false);
    }
  }, [externalCommissionId, navigate]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await externalCommissionService.update(externalCommissionId, { remarks });
      Alert.success('Commission record updated');
      navigate(`/external-commissions/view/${externalCommissionId}`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n) => `₹${Math.round(parseFloat(n || 0)).toLocaleString('en-IN')}`;
  const agents = record?.booked_agent_commission_json?.agents || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading commission..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!record) return null;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-buttonhoverTxt shrink-0" />
            Edit Commission Record
          </h1>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate(`/external-commissions/view/${externalCommissionId}`)}>
            Back
          </Button>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
            <div><span className="text-gray-500">Commission ID</span><p className="font-semibold">{record.external_commission_id}</p></div>
            <div><span className="text-gray-500">ML Sheet</span><p className="font-semibold">{record.ml_sheet_id}</p></div>
            <div><span className="text-gray-500">Vehicle Owner</span><p className="font-semibold">{record.vehicle_owner_name}</p></div>
            <div><span className="text-gray-500">Booking Agent</span><p className="font-semibold">{bookingAgentSummary(record)}</p></div>
            <div><span className="text-gray-500">Total Freight</span><p className="font-semibold">{fmt(record.total_freight)}</p></div>
            <div><span className="text-gray-500">To Pay at Destination</span><p className="font-semibold">{fmt(record.balance_freight)}</p></div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Settlement Summary (read-only)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-3 text-sm">
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Agent Commission</p>
                <p className="font-bold text-purple-700">{fmt(effectiveAgentCommission(record))}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Vehicle Owner Share</p>
                <p className="font-bold text-green-700">{fmt(effectiveOwnerShare(record))}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Receive from Agent</p>
                <p className="font-bold text-orange-700">{fmt(effectiveAgentRemit(record))}</p>
                <p className="text-xs text-gray-500 mt-1">Remaining: {fmt(remainingAgentRemit(record))}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Pay to Owner</p>
                <p className="font-bold text-blue-700">{fmt(effectiveOwnerPayable(record))}</p>
                <p className="text-xs text-gray-500 mt-1">Remaining: {fmt(remainingOwnerPay(record))}</p>
              </div>
              {effectiveAgentPayable(record) > 0 && (
                <div className="bg-indigo-50 rounded-lg p-3 col-span-2 sm:col-span-2">
                  <p className="text-xs text-gray-500">Pay to Agent (commission not retained)</p>
                  <p className="font-bold text-indigo-700">{fmt(effectiveAgentPayable(record))}</p>
                  <p className="text-xs text-gray-500 mt-1">Remaining: {fmt(remainingAgentPay(record))}</p>
                </div>
              )}
            </div>

            {agents.length > 0 && (
              <>
              <div className="mt-4 hidden md:block overflow-x-auto">
                <table className="w-full min-w-[480px] text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600">
                      <th className="px-2 py-2 text-left">Agent</th>
                      <th className="px-2 py-2 text-right">Freight</th>
                      <th className="px-2 py-2 text-right">Commission</th>
                      <th className="px-2 py-2 text-right">Remit</th>
                      <th className="px-2 py-2 text-right">Owner (to-pay)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent) => {
                      const ownerPct = effectiveOwnerCommissionPercent(record, agent);
                      const pctLabel = ownerPct != null && !Number.isNaN(ownerPct)
                        ? ` (${ownerPct}% owner)`
                        : '';
                      return (
                      <tr key={agent.agent_id} className="border-t">
                        <td className="px-2 py-2">{agent.agent_name}{pctLabel}</td>
                        <td className="px-2 py-2 text-right">{fmt(agent.total_freight)}</td>
                        <td className="px-2 py-2 text-right">{fmt(agent.agent_commission_amount)}</td>
                        <td className="px-2 py-2 text-right">{fmt(agent.agent_remit_to_owner)}</td>
                        <td className="px-2 py-2 text-right">{fmt(agent.owner_share_on_to_pay)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 md:hidden space-y-3">
                {agents.map((agent) => {
                  const ownerPct = effectiveOwnerCommissionPercent(record, agent);
                  const pctLabel = ownerPct != null && !Number.isNaN(ownerPct)
                    ? ` (${ownerPct}% owner)`
                    : '';
                  return (
                    <div key={agent.agent_id} className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                      <p className="text-sm font-semibold text-gray-800 mb-2">{agent.agent_name}{pctLabel}</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-gray-500">Freight:</span> <span className="font-semibold">{fmt(agent.total_freight)}</span></div>
                        <div><span className="text-gray-500">Commission:</span> <span className="font-semibold">{fmt(agent.agent_commission_amount)}</span></div>
                        <div><span className="text-gray-500">Remit:</span> <span className="font-semibold">{fmt(agent.agent_remit_to_owner)}</span></div>
                        <div><span className="text-gray-500">Owner (to-pay):</span> <span className="font-semibold">{fmt(agent.owner_share_on_to_pay)}</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Optional notes for this commission record"
              />
              <p className="text-xs text-gray-500 mt-1">
                Settlement amounts are calculated from booking agents and shipments on the ML sheet. Only remarks can be edited here.
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</Button>
              <Button type="button" variant="secondary" onClick={() => navigate(`/external-commissions/view/${externalCommissionId}`)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EditExternalCommission;
